import { z } from "zod";
import { DateOnly, Money } from "./common.ts";

/** A range as people enter it: from + to (inclusive) or from + count, linked. */
export const RangeInput = z
  .object({
    from: z.number().int().min(1).max(9_007_199_254_740_000),
    to: z.number().int().min(1).max(9_007_199_254_740_000).optional(),
    count: z.number().int().min(1).max(10_000_000).optional(),
  })
  .refine((r) => r.to !== undefined || r.count !== undefined, { message: "Give the last number or the count" })
  .refine((r) => r.to === undefined || r.to >= r.from, { message: "The last number cannot be before the first" });
export type RangeInput = z.infer<typeof RangeInput>;

/** Normalise entry to inclusive first/last + count. */
export function normalizeRange(r: RangeInput): { first: number; last: number; count: number } {
  const last = r.to !== undefined ? r.to : r.from + (r.count ?? 1) - 1;
  return { first: r.from, last, count: last - r.from + 1 };
}

export const SEAL_HOLDER_KINDS = ["person", "company"] as const;
export const SEAL_TARGET_TYPES = ["production", "esrog_allocation", "stam_item", "tzitzis_batch", "packaging_record"] as const;
export const SEAL_DISPOSITIONS = ["applied", "void", "handed_off"] as const;

export const SealProgrammeInput = z.object({
  key: z.string().min(1).max(40).regex(/^[a-z0-9_]+$/),
  name: z.string().min(1).max(120),
  holderKind: z.enum(SEAL_HOLDER_KINDS),
  holderRoles: z.array(z.string().max(40)).default([]),
  targetType: z.enum(SEAL_TARGET_TYPES),
  requiresWorker: z.boolean().default(false),
  chargesPerSeal: z.boolean().default(true),
  chargeBasis: z.enum(["per_seal_on_assignment", "per_seal_on_application"]).default("per_seal_on_application"),
  active: z.boolean().default(true),
});
export const SealProgrammeUpdateInput = SealProgrammeInput.partial().omit({ key: true });

export const PrintBatchInput = z.object({
  range: RangeInput,
  printedOn: DateOnly,
  vendor: z.string().max(200).nullable().optional(),
  storageLocationKey: z.string().max(60).nullable().optional(),
  printFormat: z.object({ width: z.number().int().min(1).max(12), prefix: z.string().max(10).optional() }).default({ width: 6 }),
  notes: z.string().max(2000).nullable().optional(),
});

export const SealRollInput = z.object({
  printBatchId: z.string().uuid(),
  programmeId: z.string().uuid(),
  range: RangeInput,
  rollRef: z.string().max(80).nullable().optional(),
  storageLocationKey: z.string().max(60).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const RollImportRow = z.object({
  rollRef: z.string().max(80).optional(),
  from: z.number().int().min(1),
  to: z.number().int().min(1),
  /** how the source counts its end: KosherSeal is exclusive, the tzitzis list inclusive */
  convention: z.enum(["inclusive", "exclusive"]).default("inclusive"),
  programmeKey: z.string().max(40),
  holderName: z.string().max(200).optional(),
  /** tzitzis: the list names the company that holds the roll, not a person */
  holderCompany: z.string().max(200).optional(),
});
export const RollImportInput = z.object({
  printBatchId: z.string().uuid(),
  rows: z.array(RollImportRow).min(1).max(5000),
  dryRun: z.boolean().default(true),
});

export const AssignRollInput = z.object({
  holderType: z.enum(SEAL_HOLDER_KINDS),
  holderId: z.string().uuid(),
  /** a sub-range; defaults to the whole roll */
  range: RangeInput.optional(),
  storageLocationKey: z.string().max(60).nullable().optional(),
  shipping: Money.optional(),
  reason: z.string().max(500).nullable().optional(),
});

export const SealEventInput = z.object({
  rollId: z.string().uuid().optional(),
  range: RangeInput,
  disposition: z.enum(SEAL_DISPOSITIONS),
  targetType: z.enum(SEAL_TARGET_TYPES).nullable().optional(),
  targetId: z.string().uuid().nullable().optional(),
  toWorkerPersonId: z.string().uuid().nullable().optional(),
  toWorkerName: z.string().max(200).nullable().optional(),
  voidReasonKey: z.string().max(60).nullable().optional(),
  appliedByPersonId: z.string().uuid().nullable().optional(),
  occurredOn: DateOnly.optional(),
  note: z.string().max(2000).nullable().optional(),
  clientRef: z.string().max(80).nullable().optional(),
});
export type SealEventInput = z.infer<typeof SealEventInput>;

export const SealRollQuery = z.object({
  programme: z.string().max(40).optional(),
  state: z.string().max(20).optional(),
  holderType: z.enum(SEAL_HOLDER_KINDS).optional(),
  holderId: z.string().uuid().optional(),
  q: z.string().max(40).optional(),
  mine: z.string().optional(),
});
